import {accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';

const getRoute = (result) => result.nestedRoute || result.NestedFolderRoute || '';

const ensureCache = (childrenMap, parentId) => {
    if (!childrenMap[parentId]) childrenMap[parentId] = {folders: new Map(), items: new Map()};
    return childrenMap[parentId];
};

const addFolder = (cache, id, name, route) => {
    if (!cache.folders.has(id)) {
        cache.folders.set(id, {id, name, type: accessItemTypeEnum.FOLDER, nestedRoute: route});
    }
};

const addItem = (cache, result, route) => {
    if (!cache.items.has(result.ProcedureIdentifier)) {
        cache.items.set(result.ProcedureIdentifier, {
            id: result.ProcedureIdentifier,
            name: result.name,
            type: accessItemTypeEnum.ITEM,
            procedureType: result.ProcedureType,
            nestedRoute: route,
            isPublished: result.IsPublished === true,
            permittedRoles: result.PermittedTrainingAndFieldRoles || null,
        });
    }
};

const toArrayCache = (childrenMap) => {
    const result = {};
    for (const [parentId, cache] of Object.entries(childrenMap)) {
        result[parentId] = {
            folders: [...cache.folders.values()],
            items: [...cache.items.values()],
        };
    }
    return result;
};

const addAncestorFolders = (parts, childrenMap, expandedNodes, folderNameMap) => {
    const projectId = parts[0];
    for (let i = 1; i < parts.length; i++) {
        const ancestorId = parts[i];
        const ancestorParent = i === 1 ? projectId : parts[i - 1];
        expandedNodes.add(ancestorId);
        const cache = ensureCache(childrenMap, ancestorParent);
        const route = parts.slice(0, i + 1).join('/');
        addFolder(cache, ancestorId, folderNameMap?.get(ancestorId) || ancestorId, route);
    }
};

const classifyResult = (result, parts, childrenMap, expandedNodes, projectNameMap, folderNameMap) => {
    const route = getRoute(result);
    const parentId = parts.length > 1 ? parts[parts.length - 1] : parts[0];

    if (result.type === 'folders') {
        const cache = ensureCache(childrenMap, parentId);
        addFolder(cache, result.FolderIdentifier, result.name, route);
        expandedNodes.add(result.FolderIdentifier);
    } else if (result.type === 'procedures') {
        const cache = ensureCache(childrenMap, parentId);
        addItem(cache, result, route);
    }
};

export const buildSearchTreeData = (searchData) => {
    if (!searchData?.results?.length) return null;

    const {results, parents} = searchData;
    const projectNameMap = new Map();
    const folderNameMap = new Map();
    for (const p of (parents?.projects || [])) projectNameMap.set(p.id, p.name);
    for (const f of (parents?.folders || [])) folderNameMap.set(f.id, f.name);

    const projectsMap = new Map();
    const childrenMap = {};
    const expandedNodes = new Set();

    for (const result of results) {
        if (result.type === 'projects') {
            const projectId = result.ProjectIdentifier;
            if (!projectsMap.has(projectId)) {
                projectsMap.set(projectId, {id: projectId, name: result.name});
            }
            continue;
        }

        const route = getRoute(result);
        const parts = route.split('/').filter(Boolean);
        if (!parts.length) continue;

        const projectId = parts[0];
        if (!projectsMap.has(projectId)) {
            const name = projectNameMap.get(projectId) || projectId;
            projectsMap.set(projectId, {id: projectId, name});
        }
        expandedNodes.add(projectId);

        classifyResult(result, parts, childrenMap, expandedNodes, projectNameMap, folderNameMap);
        addAncestorFolders(parts, childrenMap, expandedNodes, folderNameMap);
    }

    return {
        projects: [...projectsMap.values()],
        childrenCache: toArrayCache(childrenMap),
        expandedNodes,
    };
};

const EMPTY_SEARCH_TREE_METHODS = {
    isLoading: false,
    loadingNodes: new Set(),
    hasMoreProjects: false,
    hasMoreChildren: () => false,
    visibleCounts: {},
    handleExpand: () => {},
    handleLoadMoreChildren: () => {},
    handleLoadMoreProjects: () => {},
    handleSearch: () => {},
};

export const buildSearchTree = (searchData) => {
    const treeData = buildSearchTreeData(searchData);
    if (!treeData) return null;
    return {...treeData, ...EMPTY_SEARCH_TREE_METHODS};
};
